import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { InfoIcon } from "~/icons/InfoIcon";
import { LocationCard } from "./Location/LocationCard";
import { ScrollArea } from "./ui/scroll-area";
import { TrashIcon } from "~/icons/TrashIcon";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useInquiryLocations } from "~/lib/tanstack-query/hooks/useInquiryLocations";
import { useAllWatchList } from "~/lib/tanstack-query/hooks/useWatchlist";
import { useTranslation } from "~/lib/tanstack-query/hooks/useTranslation";
import { TranslationContent } from "~/components/TranslationContent";
import { Checkbox } from "./ui/checkbox";
import { useStore } from "@tanstack/react-store";
import {
  closeInquiry,
  InquiryStore,
  setInquiryCurrentStep,
  setInquiryFormData,
  resetInquiryFormData,
  openInquiry,
  setInquirySelectedLocations,
} from "~/lib/stores/inquiry";
import {
  useClearInquiryLocationsMutation,
  useRemoveInquiryLocationMutation,
  useSetInquiryLocationMutation,
  useAddInquiryLocationMutation,
} from "~/lib/tanstack-query/hooks/mutations/inquiryLocations";
import { useRemoveLocationFromWatchListMutation } from "~/lib/tanstack-query/hooks/mutations/watchlist";
import { useCreateLocationInquiryMutation } from "~/lib/tanstack-query/hooks/mutations/locationInquiry";
import { cn } from "~/lib/utils";
import { Steps } from "./ui/steps";
import z from "zod";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { LoaderIcon, Mail, X } from "lucide-react";
import { CalendarAddIcon } from "~/icons/CalendarAddIcon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useEventTypes } from "~/lib/tanstack-query/hooks/useEventTypes";
import { Textarea } from "./ui/textarea";
import successImage from "~/assets/inquiry-success.jpg";
import { useState, useEffect, useRef, Fragment, useLayoutEffect } from "react";
import { LocationWithImages } from "~/lib/server-functions/get-locations";
import { HeartLargeIcon } from "~/icons/HeartLarge";
import { authClient, useAuthentication } from "~/lib/better-auth/client";
import { DatePicker } from "~/components/ui/date-picker";
import { TimePicker } from "~/components/ui/time-picker";
import { PhoneInput } from "~/components/ui/phone-input";
import { eventTypesSequence } from "~/utils/sequences";

export function LocationDrawer({
  showWatchListAccessor = true,
}: {
  showWatchListAccessor?: boolean;
}) {
  const isOpen = useStore(InquiryStore, (state) => state.isOpen);
  const inquiryCurrentStep = useStore(
    InquiryStore,
    (state) => state.inquiryCurrentStep,
  );
  const inquiryMode = useStore(InquiryStore, (state) => state.inquiryMode);
  const { data: watchListLocations } = useAllWatchList();
  const { userSession } = useAuthentication();

  return (
    <>
      {showWatchListAccessor && (
        <WatchListAccessor
          active={
            !!(watchListLocations?.length && watchListLocations.length > 0)
          }
          onStartInquiry={() => {
            openInquiry("watchlist", []);
          }}
          numberOfLocations={watchListLocations?.length ?? 0}
        />
      )}
      <Dialog open={isOpen} onOpenChange={closeInquiry}>
        <DialogContent
          closeButtonVariant="white"
          className="w-[720px] max-h-[90vh] p-0 gap-0 flex flex-col data-[state=open]:slide-in-from-bottom-4"
        >
          <DialogHeader
            variant="black"
            className="flex flex-row items-center justify-between px-6 py-3 space-y-0 shrink-0"
          >
            <DialogTitle className="text-sm font-medium">
              Locationanfrage
            </DialogTitle>
          </DialogHeader>
          <div className="bg-[#F9F9F9] p-5 flex-1 overflow-y-auto min-h-0">
            {inquiryMode === "watchlist" && (
              <WatchlistSelection
                watchListLocations={watchListLocations ?? []}
                active={inquiryCurrentStep === "list"}
              />
            )}
            <ContactForm
              active={inquiryCurrentStep === "contactForm"}
              isAuthenticated={!!userSession}
            />
            <Schedule active={inquiryCurrentStep === "schedule"} />
            <Details active={inquiryCurrentStep === "details"} />
            <Success active={inquiryCurrentStep === "success"} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const WatchListAccessor = ({
  active,
  onStartInquiry,
  numberOfLocations,
}: {
  active?: boolean;
  onStartInquiry: () => void;
  numberOfLocations: number;
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const prevNumberOfLocationsRef = useRef(numberOfLocations);
  const { data: astonTitle } = useTranslation("watchlist", "astonTitle");
  const { data: astonDescription } = useTranslation(
    "watchlist",
    "astonDescription",
  );

  useEffect(() => {
    if (prevNumberOfLocationsRef.current !== numberOfLocations) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);
      prevNumberOfLocationsRef.current = numberOfLocations;
      return () => clearTimeout(timer);
    }
  }, [numberOfLocations]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      "--location-drawer-height",
      active ? "80px" : "0px",
    );
  }, [active]);

  if (!active) return null;
  return (
    <div className="watchlist-aston fixed z-20 bottom-0 left-0 right-0 bg-black text-white px-0 py-2 md:p-4 layout-content-grid">
      <div className="flex justify-between items-center layout-place-outer-wrap">
        <div className="flex items-center gap-5 md:gap-6 mr-2.5 md:mr-0">
          <span className="relative">
            <HeartLargeIcon
              className={cn(
                "size-8 text-primary transition-transform duration-300",
                isAnimating && "scale-125",
              )}
            />
            <span className="absolute inset-0 text-black font-medium text-center flex items-center justify-center text-xs">
              {numberOfLocations}
            </span>
          </span>
          <p className="flex flex-col gap-0">
            <TranslationContent
              content={astonTitle}
              as="span"
              className="text-md"
            />
            <TranslationContent
              content={astonDescription}
              as="span"
              className="text-xs"
            />
          </p>
        </div>
        <Button variant={"default"} onClick={onStartInquiry}>
          <span className="md:hidden">Anfrage</span>
          <span className="hidden md:inline">Anfrage starten</span>
        </Button>
      </div>
    </div>
  );
};

const WatchlistSelection = ({
  watchListLocations,
  active,
}: {
  watchListLocations: LocationWithImages[];
  active?: boolean;
}) => {
  const selectedLocationIds = useStore(
    InquiryStore,
    (state) => state.selectedLocationIds,
  );
  const { userSession } = useAuthentication();
  const { mutate: setInquiryLocation } = useSetInquiryLocationMutation();
  const { mutate: addInquiryLocation } = useAddInquiryLocationMutation();
  const { mutate: removeLocationFromWatchList } =
    useRemoveLocationFromWatchListMutation();
  // Show delete button in this drawer only for non-logged-in users; logged-in users remove items only on "Meine Merkliste" page
  const showDeleteInDrawer = !userSession;

  const handleToggleSelection = (locationId: number) => {
    const isSelected = selectedLocationIds.includes(locationId);
    if (isSelected) {
      setInquirySelectedLocations(
        selectedLocationIds.filter((id) => id !== locationId),
      );
    } else {
      if (selectedLocationIds.length >= 10) {
        return; // Max 10 selections
      }
      setInquirySelectedLocations([...selectedLocationIds, locationId]);
    }
  };

  const handleDeleteFromWatchlist = (locationId: number) => {
    removeLocationFromWatchList(locationId);
    // Also remove from selection if it was selected
    if (selectedLocationIds.includes(locationId)) {
      setInquirySelectedLocations(
        selectedLocationIds.filter((id) => id !== locationId),
      );
    }
  };

  const handleStartInquiry = () => {
    if (selectedLocationIds.length === 0) return;

    // Set all selected locations to inquiry locations
    selectedLocationIds.forEach((locationId, index) => {
      if (index === 0) {
        setInquiryLocation(locationId);
      } else {
        addInquiryLocation(locationId);
      }
    });

    setInquiryCurrentStep("contactForm");
  };

  if (!active) return null;
  return (
    <>
      <ScrollArea className="bg-white p-3 rounded-md">
        <Heading>Deine Merkliste</Heading>
        <Description>
          Wähle bis zu 10 Locations aus, für die du eine Anfrage stellen
          möchtest.
        </Description>

        {selectedLocationIds.length > 0 && (
          <div className="mb-4 text-sm text-neutral-600">
            {selectedLocationIds.length} von maximal 10 Locations ausgewählt
          </div>
        )}

        <div className="grid gap-8">
          {watchListLocations.map((location) => {
            const isSelected = selectedLocationIds.includes(location.id);
            return (
              <div key={location.id} className="relative group">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id={`watchlist-${location.id}`}
                    checked={isSelected}
                    onCheckedChange={() => handleToggleSelection(location.id)}
                    disabled={!isSelected && selectedLocationIds.length >= 10}
                    className="mt-2"
                  />
                  <div className="flex-1 relative">
                    <LocationCard location={{ ...location }} compact />
                    {showDeleteInDrawer && (
                      <Button
                        size={"icon"}
                        variant={"flatDark"}
                        className="absolute bottom-0 right-0 opacity-50 group-hover:opacity-100"
                        onClick={() => handleDeleteFromWatchlist(location.id)}
                        title="Aus der Merkliste entfernen"
                      >
                        <TrashIcon />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
      <div className="flex flex-wrap justify-between gap-4 mt-3">
        <DialogClose asChild>
          <Button variant={"outline"}>Weitere Locations hinzufügen</Button>
        </DialogClose>
        <Button
          onClick={handleStartInquiry}
          disabled={selectedLocationIds.length === 0}
        >
          Anfrage starten ({selectedLocationIds.length})
        </Button>
      </div>
    </>
  );
};

const ContactForm = ({
  active,
  isAuthenticated = false,
}: {
  active?: boolean;
  isAuthenticated?: boolean;
}) => {
  const inquiryMode = useStore(InquiryStore, (state) => state.inquiryMode);
  const formData = useStore(InquiryStore, (state) => state.formData);
  const formSchema = z
    .object({
      salutation: z.enum(["Frau", "Herr", "Divers"], {
        message: "Bitte wählen Sie eine Anrede",
      }),
      firstName: z
        .string()
        .min(1, "Vorname ist erforderlich")
        .max(100, "Vorname ist zu lang"),
      lastName: z
        .string()
        .min(1, "Nachname ist erforderlich")
        .max(100, "Nachname ist zu lang"),
      company: z.string().max(200, "Firmenname ist zu lang").optional(),
      email: z.email("Ungültige E-Mail-Adresse"),
      telephone: z
        .string()
        .min(1, "Telefonnummer ist erforderlich")
        .refine((val) => isValidPhoneNumber(val, "DE"), {
          message: "Ungültige Telefonnummer",
        }),
      createAccount: z.boolean().optional(),
      password: z
        .string()
        .min(8, "Passwort muss mindestens 8 Zeichen lang sein")
        .optional(),
    })
    .superRefine((data, ctx) => {
      if (data.createAccount) {
        if (!data.password || data.password.length < 8) {
          ctx.addIssue({
            path: ["password"],
            message: "Passwort ist erforderlich",
            code: "custom",
          });
        }
      }
    });
  const form = useForm({
    defaultValues: {
      salutation: formData.salutation ?? "Frau",
      firstName: formData.firstName ?? "",
      lastName: formData.lastName ?? "",
      company: formData.company ?? "",
      email: formData.email ?? "",
      telephone: formData.telephone ?? "",
    },
    resolver: zodResolver(formSchema),
  });

  const handleSignupError = (error: any) => {
    console.log(error);
    if (
      error.code === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL" ||
      error.message?.toLowerCase().includes("already")
    ) {
      form.setError("email", {
        type: "manual",
        message: "Für diese E-Mail existiert bereits ein Benutzerkonto",
      });
      return;
    }

    form.setError("password", {
      type: "manual",
      message: "Registrierung fehlgeschlagen. Bitte versuche es erneut.",
    });
  };

  const [registerLoading, setRegisterLoading] = useState(false);

  if (!active) return null;
  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(async (data) => {
            let userId: string | undefined = undefined;
            if (data.createAccount) {
              setRegisterLoading(true);
              const { error, data: signUpData } = await authClient.signUp.email(
                {
                  email: data.email,
                  password: data.password!,
                  firstName: data.firstName,
                  lastName: data.lastName,
                  name: `${data.firstName} ${data.lastName}`,
                  company: data.company ?? "",
                  salutation: data.salutation,
                  telephone: data.telephone,
                  preferredLanguage: "german",
                  isAdvertiser: false,
                },
              );

              if (error) {
                handleSignupError(error);
                setRegisterLoading(false);
                return;
              }

              userId = signUpData?.user?.id;
              setRegisterLoading(false);
            }

            setInquiryFormData({
              userId,
              salutation: data.salutation,
              firstName: data.firstName,
              lastName: data.lastName,
              company: data.company,
              email: data.email,
              telephone: data.telephone,
            });
            setInquiryCurrentStep("schedule");
          })}
        >
          <ScrollArea className="bg-white rounded-md">
            <div className="p-3 overflow-visible">
              <Steps
                numberOfSteps={3}
                currentStep={1}
                variant="bubbles"
                className="mb-6"
              />
              <Heading>Deine Kontaktdaten</Heading>
              <Description className="sr-only">Anfrage</Description>
              <div className={cn("flex flex-col gap-4")}>
                <FormField
                  control={form.control}
                  name="salutation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Anrede*</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex gap-8 mt-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Frau" id="frau" />
                            <Label htmlFor="frau">Frau</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Herr" id="herr" />
                            <Label htmlFor="herr">Herr</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Divers" id="divers" />
                            <Label htmlFor="divers">Divers</Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vorname*</FormLabel>
                      <FormControl>
                        <Input placeholder="Vorname" {...field} />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nachname*</FormLabel>
                      <FormControl>
                        <Input placeholder="Nachname" {...field} />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Firma</FormLabel>
                      <FormControl>
                        <Input placeholder="Firma" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-Mail*</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="E-Mail"
                          iconStart={<Mail />}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="telephone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefonnummer*</FormLabel>
                      <FormControl>
                        <PhoneInput {...field} />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                {!isAuthenticated && (
                  <>
                    <FormField
                      control={form.control}
                      name="createAccount"
                      render={({ field }) => (
                        <FormItem className="flex items-center">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel>
                            Benutzerkonto erstellen und Überblick über alle
                            Anfragen behalten
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                    {form.watch("createAccount") && (
                      <div>
                        <FormField
                          control={form.control}
                          name="password"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Passwort*</FormLabel>
                              <FormControl>
                                <Input placeholder="Passwort" {...field} />
                              </FormControl>
                              <FormMessage className="text-red-500" />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                    {form.watch("createAccount") && (
                      <p className="text-sm font-light">
                        Mit deiner Registrierung erklärst du dich mit unseren
                        AGB und Datenschutzbestimmungen einverstanden.
                        <br />
                        Diese Seite ist durch reCAPTCHA geschützt und es gelten
                        die Google{" "}
                        <a
                          target="_blank"
                          href="https://policies.google.com/privacy"
                          className="underline underline-offset-2"
                        >
                          Datenschutzbestimmungen
                        </a>{" "}
                        und{" "}
                        <a
                          target="_blank"
                          href="https://policies.google.com/terms"
                          className="underline underline-offset-2"
                        >
                          Nutzungsbedingungen
                        </a>
                        .
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </ScrollArea>
          <div className="flex flex-wrap justify-between gap-4 mt-3">
            <DialogClose asChild>
              <Button variant={"outline"}>Weitere Locations hinzufügen</Button>
            </DialogClose>
            <div className="flex justify-end gap-4">
              {inquiryMode === "watchlist" && (
                <Button
                  variant={"black"}
                  onClick={() => setInquiryCurrentStep("list")}
                >
                  Zurück
                </Button>
              )}
              <Button type="submit">
                {registerLoading ? (
                  <LoaderIcon className="animate-spin" />
                ) : null}
                Weiter
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  );
};

type Appointment = {
  start?: Date;
  end?: Date;
};

const Schedule = ({ active }: { active?: boolean }) => {
  const formData = useStore(InquiryStore, (state) => state.formData);
  const isOpen = useStore(InquiryStore, (state) => state.isOpen);
  const prevIsOpenRef = useRef(false);
  const [appointments, setAppointments] = useState<Appointment[]>([
    {
      start: undefined,
      end: undefined,
    },
  ]);
  const [validationError, setValidationError] = useState<string>("");

  // Reset appointments when dialog is opened (when isOpen changes from false to true)
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current && active) {
      setAppointments([
        {
          start: undefined,
          end: undefined,
        },
      ]);
      setValidationError("");
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, active]);

  const validateAppointments = (apps: Appointment[]): boolean => {
    // check if at least one appointment has a start and end date
    const validAppointments = apps.filter(
      (app) =>
        app.start &&
        app.end &&
        app.start instanceof Date &&
        app.end instanceof Date,
    );

    if (validAppointments.length === 0) {
      setValidationError("Mindestens einen gültigen Wunschtermin eintragen");
      return false;
    }

    setValidationError("");
    return true;
  };

  // Validate appointments whenever they change (to clear error message when valid)
  useEffect(() => {
    if (active && validationError) {
      const validAppointments = appointments.filter(
        (app) =>
          app.start &&
          app.end &&
          app.start instanceof Date &&
          app.end instanceof Date,
      );

      if (validAppointments.length > 0) {
        setValidationError("");
      }
    }
  }, [appointments, active, validationError]);

  const ensureEndAfterStart = (start: Date, end: Date) => {
    return end < start ? new Date(start) : end;
  };

  const setDatePart = (base: Date, date: Date) => {
    const d = new Date(base);
    d.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    return d;
  };

  const setTimePart = (base: Date, time: Date) => {
    const d = new Date(base);
    d.setHours(
      time.getHours(),
      time.getMinutes(),
      time.getSeconds(),
      time.getMilliseconds(),
    );
    return d;
  };

  if (!active) return null;

  return (
    <>
      <ScrollArea className="bg-white rounded-md">
        <div className="p-3 overflow-visible">
          <Steps
            numberOfSteps={3}
            currentStep={2}
            variant="bubbles"
            className="mb-6"
          />
          <Heading>Wunschtermin</Heading>
          <Description className="sr-only">Anfrage</Description>

          <div className="flex lg:flex-col flex-row gap-8">
            {appointments.map((appointment, idx) => (
              <div key={idx}>
                <p className="mb-1 flex justify-between items-center">
                  <span>
                    Option {idx + 1} {idx === 0 ? "*" : ""}
                  </span>
                  {idx > 0 ? (
                    <Button
                      onClick={() =>
                        setAppointments(
                          appointments.filter((_, i) => i !== idx),
                        )
                      }
                      size={"icon"}
                      variant={"ghost"}
                    >
                      <X />
                    </Button>
                  ) : null}
                </p>
                <p className="font-semibold mb-2">Datum</p>
                <div className="flex flex-wrap gap-4 w-full">
                  <DatePicker
                    value={appointment.start}
                    minDate={new Date()}
                    onChange={(date) => {
                      if (!date) return;

                      const updated = [...appointments];
                      const current = updated[idx];

                      const newStart = setDatePart(
                        current.start ?? new Date(),
                        date,
                      );

                      // If end is undefined, set it to newStart (same date as start)
                      // Otherwise ensure end is after start
                      const newEnd = current.end
                        ? ensureEndAfterStart(
                          newStart,
                          setDatePart(current.end, date),
                        )
                        : new Date(newStart);

                      updated[idx] = {
                        ...current,
                        start: newStart,
                        end: newEnd,
                      };

                      setAppointments(updated);
                    }}
                  />
                  <DatePicker
                    value={appointment.end}
                    minDate={appointment.start ?? new Date()}
                    onChange={(date) => {
                      if (!date) return;

                      const updated = [...appointments];
                      const current = updated[idx];

                      const newEnd = setDatePart(
                        current.end ?? new Date(),
                        date,
                      );

                      updated[idx] = {
                        ...current,
                        end: ensureEndAfterStart(
                          current.start ?? new Date(),
                          newEnd,
                        ),
                      };

                      setAppointments(updated);
                    }}
                    label="bis"
                  />
                </div>
                <p className="font-semibold my-2">Uhrzeit</p>
                <div className="flex flex-wrap gap-4 w-full">
                  <TimePicker
                    value={appointment.start}
                    onChange={(date) => {
                      if (!date) return;

                      const updated = [...appointments];
                      const current = updated[idx];

                      const newStart = setTimePart(
                        current.start ?? new Date(),
                        date,
                      );

                      // If end is undefined, set it to newStart (same time as start)
                      // Otherwise ensure end is after start
                      const newEnd = current.end
                        ? ensureEndAfterStart(newStart, current.end)
                        : new Date(newStart);

                      updated[idx] = {
                        ...current,
                        start: newStart,
                        end: newEnd,
                      };

                      setAppointments(updated);
                    }}
                  />
                  <TimePicker
                    value={appointment.end}
                    onChange={(date) => {
                      if (!date) return;

                      const updated = [...appointments];
                      const current = updated[idx];

                      const newEnd = setTimePart(
                        current.end ?? new Date(),
                        date,
                      );

                      updated[idx] = {
                        ...current,
                        end: ensureEndAfterStart(
                          current.start ?? new Date(),
                          newEnd,
                        ),
                      };

                      setAppointments(updated);
                    }}
                    label="bis"
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-col items-start gap-4">
            {validationError && (
              <p className="text-sm text-red-500">{validationError}</p>
            )}
            <Button
              className="text-neutral-dark px-0"
              variant={"link"}
              onClick={() =>
                setAppointments([
                  ...appointments,
                  {
                    start: undefined,
                    end: undefined,
                  },
                ])
              }
            >
              Weitere Wunschdaten hinzufügen
              <CalendarAddIcon className="size-5" />
            </Button>

            <p className="text-sm font-light">
              Diese Seite ist durch reCAPTCHA geschützt und es gelten die Google{" "}
              <a
                target="_blank"
                href="https://policies.google.com/privacy"
                className="underline underline-offset-2"
              >
                Datenschutzbestimmungen
              </a>{" "}
              und{" "}
              <a
                target="_blank"
                href="https://policies.google.com/terms"
                className="underline underline-offset-2"
              >
                Nutzungsbedingungen
              </a>
              .
            </p>
          </div>
        </div>
      </ScrollArea>
      <div className="flex flex-wrap justify-between gap-4 mt-3">
        <DialogClose asChild>
          <Button variant={"outline"}>Weitere Locations hinzufügen</Button>
        </DialogClose>
        <div className="flex justify-end gap-4">
          <Button
            variant={"black"}
            onClick={() => setInquiryCurrentStep("contactForm")}
          >
            Zurück
          </Button>
          <Button
            onClick={() => {
              if (validateAppointments(appointments)) {
                const validAppointments = appointments.filter(
                  (app): app is { start: Date; end: Date } =>
                    app.start instanceof Date && app.end instanceof Date,
                );
                setInquiryFormData({ appointments: validAppointments });
                setInquiryCurrentStep("details");
              }
            }}
          >
            Weiter
          </Button>
        </div>
      </div>
    </>
  );
};

const Details = ({ active }: { active?: boolean }) => {
  const formData = useStore(InquiryStore, (state) => state.formData);
  const formSchema = z.object({
    eventType: z.string().min(1, "Anlass ist erforderlich"),

    businessType: z.enum(["Privat", "Geschäftlich"], {
      message: "Bitte wählen Sie eine Kategorie",
    }),

    numberOfRooms: z
      .number({
        message: "Anzahl der Räume ist erforderlich",
      })
      .int("Muss eine ganze Zahl sein")
      .positive("Muss größer als 0 sein")
      .optional(),

    numberOfGuests: z
      .number({
        message: "Anzahl der Gäste ist erforderlich",
      })
      .int("Muss eine ganze Zahl sein")
      .positive("Muss größer als 0 sein"),

    budgetType: z.enum(["Gesamt", "Pro Person"], {
      message: "Bitte wählen Sie einen Budget-Typ",
    }),

    budget: z
      .number({
        message: "Budget ist erforderlich",
      })
      .positive("Budget muss größer als 0 sein")
      .optional(),

    budgetCurrency: z.string().min(1, "Währung ist erforderlich"),

    notes: z.string().optional(),

    selectedRooms: z
      .array(
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      )
      .optional(),
  });
  const form = useForm({
    defaultValues: {
      eventType:
        formData.eventTypeId != null ? String(formData.eventTypeId) : "",
      businessType: formData.businessType ?? "Privat",
      numberOfRooms: formData.numberOfRooms,
      numberOfGuests: formData.numberOfGuests,
      budgetType: formData.budgetType ?? "Gesamt",
      budget: formData.budget,
      budgetCurrency: formData.budgetCurrency ?? "EUR",
      notes: formData.notes ?? "",
      selectedRooms: formData.requestedRooms ?? [],
    },
    resolver: zodResolver(formSchema),
  });
  const { data: eventTypes } = useEventTypes(eventTypesSequence);
  const { data: inquiryLocations } = useInquiryLocations();
  const inquiryMode = useStore(InquiryStore, (state) => state.inquiryMode);
  const selectedLocationIds = useStore(
    InquiryStore,
    (state) => state.selectedLocationIds,
  );

  const [isLoading, setIsLoading] = useState(false);
  const { mutate: clearInquiryLocations } = useClearInquiryLocationsMutation();
  const { mutate: createLocationInquiry } = useCreateLocationInquiryMutation();
  const { mutateAsync: removeLocationFromWatchList } =
    useRemoveLocationFromWatchListMutation();

  // Get rooms from the selected location if only one is selected
  const selectedLocation =
    inquiryLocations && inquiryLocations.length === 1
      ? inquiryLocations[0]
      : null;
  const availableRooms = selectedLocation?.rooms ?? [];

  if (!active) return null;
  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(async (data) => {
            if (!inquiryLocations || inquiryLocations.length === 0) {
              return;
            }

            if (
              !formData.salutation ||
              !formData.firstName ||
              !formData.lastName ||
              !formData.email ||
              !formData.telephone ||
              !formData.appointments ||
              formData.appointments.length === 0
            ) {
              return;
            }

            setIsLoading(true);

            try {
              await new Promise<void>((resolve, reject) => {
                createLocationInquiry(
                  {
                    userId: formData.userId,
                    locationIds: inquiryLocations.map((loc) => loc.id),
                    salutation: formData.salutation!,
                    firstName: formData.firstName!,
                    lastName: formData.lastName!,
                    company: formData.company,
                    email: formData.email!,
                    telephone: formData.telephone!,
                    appointments: formData.appointments!,
                    eventTypeId: Number(data.eventType),
                    businessType: data.businessType,
                    numberOfRooms: data.numberOfRooms,
                    numberOfGuests: data.numberOfGuests,
                    budgetType: data.budgetType,
                    budget: data.budget,
                    budgetCurrency: data.budgetCurrency,
                    notes: data.notes,
                    requestedRooms: data.selectedRooms,
                  },
                  {
                    onSuccess: async () => {
                      // Remove all inquired locations from watchlist (single or multiple)
                      await Promise.all(
                        inquiryLocations.map((loc) =>
                          removeLocationFromWatchList(loc.id),
                        ),
                      );
                      clearInquiryLocations();
                      resetInquiryFormData();
                      resolve();
                    },
                    onError: (error) => {
                      reject(error);
                    },
                  },
                );
              });
              setInquiryCurrentStep("success");
            } catch (error) {
              console.error("Failed to create location inquiry:", error);
            } finally {
              setIsLoading(false);
            }
          })}
        >
          <ScrollArea className="bg-white rounded-md">
            <div className="p-3 overflow-visible">
              <Steps
                numberOfSteps={3}
                currentStep={3}
                variant="bubbles"
                className="mb-6"
              />
              <Heading>Details zu Deinem Event</Heading>
              <Description className="sr-only">Anfrage</Description>
              <div className={cn("flex flex-col gap-4")}>
                <FormField
                  control={form.control}
                  name="eventType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Anlass*</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value || ""}
                          onValueChange={(value) => field.onChange(value)}
                        >
                          <SelectTrigger
                            aria-invalid={!!form.formState.errors.eventType}
                            className="w-full"
                            variant="outline"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {eventTypes?.map((eventType) => (
                              <SelectItem
                                key={eventType.id}
                                value={eventType.id.toString()}
                              >
                                {eventType.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="businessType"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex gap-8 mb-2"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Privat" id="privat" />
                            <Label htmlFor="privat">Privat</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="Geschäftlich"
                              id="geschaeftlich"
                            />
                            <Label htmlFor="geschaeftlich">Geschäftlich</Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="numberOfRooms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Anzahl Räume</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Angeben"
                          type="number"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "") {
                              field.onChange(undefined);
                            } else {
                              field.onChange(Number(value));
                            }
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                {selectedLocation && availableRooms.length > 0 && (
                  <FormField
                    control={form.control}
                    name="selectedRooms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Räume anfragen</FormLabel>
                        <FormControl>
                          <div className="grid grid-cols-2 gap-4">
                            {availableRooms.map((room) => (
                              <div className="flex gap-2" key={room.id}>
                                <Checkbox
                                  id={`room-${room.id}`}
                                  checked={
                                    field.value?.some(
                                      (r) => r.id === room.id,
                                    ) ?? false
                                  }
                                  onCheckedChange={(checked) => {
                                    const currentValue = field.value ?? [];
                                    if (checked) {
                                      field.onChange([
                                        ...currentValue,
                                        { id: room.id, name: room.name },
                                      ]);
                                    } else {
                                      field.onChange(
                                        currentValue.filter(
                                          (r) => r.id !== room.id,
                                        ),
                                      );
                                    }
                                  }}
                                />
                                <Label htmlFor={`room-${room.id}`}>
                                  {room.name}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name="numberOfGuests"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Anzahl Teilnehmer/Gäste *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Angeben"
                          type="number"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "") {
                              field.onChange(undefined);
                            } else {
                              field.onChange(Number(value));
                            }
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="budgetType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Budget</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex gap-8 mb-2"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Gesamt" id="gesamt" />
                            <Label htmlFor="gesamt">Gesamt</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="Pro Person"
                              id="pro-person"
                            />
                            <Label htmlFor="pro-person">Pro Person</Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="flex gap-4">
                  <FormField
                    control={form.control}
                    name="budget"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            placeholder="Angeben"
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                field.onChange(undefined);
                              } else {
                                field.onChange(Number(value));
                              }
                            }}
                            type="number"
                            step={10}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="budgetCurrency"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Select
                            value={field.value || ""}
                            onValueChange={(value) => field.onChange(value)}
                          >
                            <SelectTrigger className="w-full" variant="outline">
                              <SelectValue>{field.value}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="EUR">EUR</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Deine Anmerkungen</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Nachricht"
                          {...field}
                          variant="outline"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <p className="text-sm font-light">
                  Diese Seite ist durch reCAPTCHA geschützt und es gelten die
                  Google{" "}
                  <a
                    target="_blank"
                    href="https://policies.google.com/privacy"
                    className="underline underline-offset-2"
                  >
                    Datenschutzbestimmungen
                  </a>{" "}
                  und{" "}
                  <a
                    target="_blank"
                    href="https://policies.google.com/terms"
                    className="underline underline-offset-2"
                  >
                    Nutzungsbedingungen
                  </a>
                  .
                </p>
              </div>
            </div>
          </ScrollArea>
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap justify-between gap-4 mt-3">
              <DialogClose asChild>
                <Button variant={"outline"}>
                  Weitere Locations hinzufügen
                </Button>
              </DialogClose>
              <div className="flex justify-end gap-4">
                <Button
                  variant={"black"}
                  onClick={() => setInquiryCurrentStep("schedule")}
                >
                  Zurück
                </Button>
                <Button type="submit" loading={isLoading}>
                  Anfrage senden
                </Button>
              </div>
            </div>
            <p className="text-sm font-light text-end">
              Mit Klick auf "Anfrage Senden" stimmen Sie unseren{" "}
              <a
                target="_blank"
                href="/agb"
                className="underline underline-offset-2"
              >
                AGB
              </a>{" "}
              zu.
            </p>
          </div>
        </form>
      </Form>
    </>
  );
};

const Success = ({ active }: { active?: boolean }) => {
  const { userSession } = useAuthentication();
  if (!active) return null;
  return (
    <>
      <div className="bg-white p-3 rounded-md">
        <Steps
          numberOfSteps={3}
          currentStep={4}
          variant="bubbles"
          className="mb-6"
        />
        <Heading>Vielen Dank für deine Anfrage!</Heading>
        {userSession && (
          <p className="text-sm text-gray-600 mb-4">
            Die angefragten Locations findest du im Bereich{" "}
            <strong>„Meine Anfragen"</strong> – dort kannst du auch jederzeit
            den aktuellen Status verfolgen.
          </p>
        )}
        <Description className="sr-only">Anfrage</Description>
        <img
          src={successImage}
          className="w-full h-auto rounded-md mt-12"
          alt=""
        />
      </div>
    </>
  );
};

const Heading = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="text-2xl font-medium leading-none tracking-tight mb-4">
      {children}
    </div>
  );
};

const Description = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-dove-gray-dark text-sm mb-6 flex items-center gap-2",
        className,
      )}
    >
      <span className="shrink-0 w-6 h-6 bg-dove-gray-dark flex items-center justify-center rounded-md text-white">
        <InfoIcon className="w-4 h-4" />
      </span>
      {children}
    </DialogPrimitive.Description>
  );
};
